// ─────────────────────────────────────────────────────────────────────────────
// Voice Mode 2.0 — verification harness support (owner directive 2026-09-20).
//
// The voice-command corpus verification harness drives REAL plans through the
// browser and asserts the WCP animation contract: highlight ring BEFORE the
// action (~220 ms), press pulse, visible character-by-character typing, and
// 80–160 ms step gaps.  Those facts are recorded here so the harness can read
// them back over CDP, and only while harness mode is ON:
//
//   localStorage.saik_harness = '1'
//
// In normal operation (flag unset) every function in this module is a no-op —
// zero behaviour change, nothing recorded, no attributes written.
// ─────────────────────────────────────────────────────────────────────────────

export interface UiTraceEntry {
  type: string;
  planId?: string;
  stepId?: string;
  agentId?: string;
  at: number;
  data?: Record<string, unknown>;
}

const HARNESS_KEY = 'saik_harness';
const MAX_TRACE_ENTRIES = 4000;

/** True when the verification harness asked this tab to record choreography. */
export function isHarnessMode(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(HARNESS_KEY) === '1';
  } catch {
    return false;
  }
}

interface HarnessGlobal {
  __saikUiTrace?: UiTraceEntry[];
  __saikLastPlan?: Record<string, unknown>;
  __saikDomEvents?: { type: string; agentId: string; at: number }[];
  __saikObserversInstalled?: boolean;
}

function g(): HarnessGlobal {
  return window as unknown as HarnessGlobal;
}

/** Append one bounded trace entry (harness mode only). */
export function recordTrace(entry: Omit<UiTraceEntry, 'at'> & { at?: number }): void {
  if (!isHarnessMode()) return;
  const w = g();
  const list = (w.__saikUiTrace ||= []);
  list.push({ ...entry, at: entry.at ?? Date.now() });
  if (list.length > MAX_TRACE_ENTRIES) list.splice(0, list.length - MAX_TRACE_ENTRIES);
}

/**
 * Install DOM observers that witness the animation contract on real elements
 * (harness mode only, idempotent):
 *  - when a highlight ring element (`[data-agent-ring]`) appears
 *  - when a press pulse (`[data-agent-press]`) is added/removed
 * These are the *user-visible* facts; the executor trace records the
 * intent-side facts (ring called, press set, chars typed).
 */
export function ensureObservers(): void {
  if (!isHarnessMode()) return;
  const w = g();
  if (w.__saikObserversInstalled) return;
  w.__saikObserversInstalled = true;
  const events = (w.__saikDomEvents ||= []);
  const push = (type: string, agentId: string) => {
    events.push({ type, agentId, at: Date.now() });
    if (events.length > MAX_TRACE_ENTRIES) events.splice(0, events.length - MAX_TRACE_ENTRIES);
  };
  const observer = new MutationObserver((records) => {
    for (const rec of records) {
      if (rec.type === 'attributes' && rec.attributeName === 'data-agent-press') {
        const el = rec.target as HTMLElement;
        const on = el.getAttribute('data-agent-press') === 'true';
        push(on ? 'press.add' : 'press.remove', el.getAttribute('data-agent-id') || 'unknown');
      }
      if (rec.type === 'childList') {
        for (const node of Array.from(rec.addedNodes)) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.hasAttribute('data-agent-ring')) {
            push('ring.add', node.getAttribute('data-agent-ring') || 'unknown');
          }
          node.querySelectorAll?.('[data-agent-ring]').forEach((child) => {
            push('ring.add', child.getAttribute('data-agent-ring') || 'unknown');
          });
        }
        for (const node of Array.from(rec.removedNodes)) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.hasAttribute('data-agent-ring')) {
            push('ring.remove', node.getAttribute('data-agent-ring') || 'unknown');
          }
        }
      }
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-agent-press'],
  });
}

/** Publish the last plan's execution summary (harness mode only). */
export function publishLastPlan(summary: Record<string, unknown>): void {
  if (!isHarnessMode()) return;
  g().__saikLastPlan = summary;
}

/** Clear trace + last-plan state (used between harness batches). */
export function resetHarnessState(): void {
  if (!isHarnessMode()) return;
  const w = g();
  w.__saikUiTrace = [];
  w.__saikLastPlan = undefined;
  w.__saikDomEvents = [];
}
